import React from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import PhoneSimulator from "../components/PhoneSimulator";
import { WaziMark } from "../components/Logo";
import { Button } from "../components/ui/button";
import { trackEvent } from "../lib/analytics";
import {
  ShieldCheck,
  MessageCircle,
  Sparkles,
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  Quote,
  Star,
  Paintbrush,
  Wrench,
  Plug,
} from "lucide-react";

const FEATURES = [
  { icon: MessageCircle, title: "WhatsApp-first", body: "No app to download. Your customers find you on the platform they already use every day.", color: "text-[#25D366]" },
  { icon: Sparkles, title: "AI quotes in seconds", body: "Wazi drafts a professional quote with line items, totals and your banking details — you just approve.", color: "text-[#1E88E5]" },
  { icon: ShieldCheck, title: "Trust built in", body: "Verified profiles, real reviews and clean payment details so customers say yes faster.", color: "text-[#16A34A]" },
  { icon: TrendingUp, title: "Win more work", body: "We send you nearby leads, you accept the ones you want. Simple as that.", color: "text-[#1E88E5]" },
];

const STEPS = [
  { num: "01", title: "Say 'Hi' on WhatsApp", body: "Painters chat with Wazi to set up their profile in under 3 minutes. No forms. No apps." },
  { num: "02", title: "Get matched with leads", body: "Customers in your area request work. We send the lead to 3–5 nearby painters." },
  { num: "03", title: "Send a pro quote", body: "Wazi drafts the quote — you tweak and send. Customer pays the deposit and you're booked." },
  { num: "04", title: "Get paid & get reviewed", body: "Finish the job, collect a 5-star review on your public profile, and win more work." },
];

const TRADES = [
  { icon: Paintbrush, label: "Painters", live: true },
  { icon: Wrench, label: "Plumbers", live: false },
  { icon: Plug, label: "Electricians", live: false },
];

const TESTIMONIALS = [
  { name: "Sipho D.", role: "Painter, Durbanville", text: "Three quotes in one afternoon. Two booked. Wazi is changing how I run my business." },
  { name: "Nomvula M.", role: "Customer, Cape Town", text: "I had 3 quotes by lunchtime. Picked Sipho because of his reviews. Job was perfect." },
  { name: "Pieter v.d. Merwe", role: "Painter, Sandton", text: "I look more professional than guys with full websites. And it's all on WhatsApp." },
];

const Landing = () => {
  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative wazi-grain overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#F7FAFC] to-white" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 bg-[#25D366]/10 text-[#16A34A] px-3 py-1.5 rounded-full text-xs font-medium font-heading">
              <span className="w-2 h-2 bg-[#25D366] rounded-full animate-pulse" />
              Now live for painters in South Africa
            </div>
            <h1 className="font-heading mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2D3748] leading-[1.05] tracking-tight">
              Your business.<br/>
              <span className="text-[#25D366]">Simplified.</span><br/>
              On WhatsApp.
            </h1>
            <p className="mt-6 text-lg text-[#718096] max-w-xl leading-relaxed">
              Wazi turns WhatsApp into your office. Get leads, send AI-drafted quotes, collect reviews and look professional - all without a website or an app.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/get-started" onClick={() => trackEvent("cta_click", { location: "hero", audience: "painter" })}>
                <Button
                  data-testid="hero-painter-cta"
                  className="rounded-full bg-[#25D366] hover:bg-[#16A34A] text-white px-7 py-6 text-base font-medium wazi-cta-glow w-full sm:w-auto"
                >
                  Start on WhatsApp — it's free
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a
                href="https://wa.me/27600702722?text=Hi%20Wazi%2C%20I%27m%20a%20painter%20and%20want%20to%20get%20started."
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("cta_click", { location: "hero", audience: "painter", action: "deep_link" })}
                data-testid="hero-wa-deeplink"
                className="sm:hidden inline-flex items-center justify-center w-full px-7 py-3 rounded-full border-2 border-[#25D366] text-[#25D366] font-heading font-medium text-sm"
              >
                Or chat directly with Wazi on WhatsApp →
              </a>
              <Link to="/find-painter" onClick={() => trackEvent("cta_click", { location: "hero", audience: "customer" })}>
                <Button
                  data-testid="hero-customer-cta"
                  variant="outline"
                  className="rounded-full border-2 border-[#1E88E5] text-[#1E88E5] hover:bg-[#1E88E5] hover:text-white px-7 py-6 text-base font-medium w-full sm:w-auto"
                >
                  I need a painter
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-[#718096]">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#16A34A]" /> No app to install</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#16A34A]" /> Free during MVP</div>
            </div>
          </div>
          <div className="relative">
            <PhoneSimulator
              role="painter"
              sessionPrefix="landing-hero"
              quickReplies={["Sipho Dlamini", "Sipho Painters", "082 555 1234", "Durbanville, Bellville", "8", "English, isiZulu", "Neat, on-time work.", "2", "FNB / Sipho Dlamini / 62812345678"]}
            />
          </div>
        </div>
      </section>

      {/* Trades */}
      <section className="bg-[#F7FAFC] py-12 border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center text-center">
          <p className="text-sm uppercase tracking-widest text-[#718096] font-heading font-semibold">Built for South African trades</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-6">
            {TRADES.map((t) => (
              <div
                key={t.label}
                className={`flex items-center gap-3 bg-white border ${t.live ? "border-[#25D366]" : "border-[#E2E8F0] opacity-70"} rounded-full px-5 py-3 lift-on-hover`}
                data-testid={`trade-${t.label.toLowerCase()}`}
              >
                <t.icon className={`w-5 h-5 ${t.live ? "text-[#25D366]" : "text-[#718096]"}`} />
                <span className="font-medium text-[#2D3748]">{t.label}</span>
                {t.live ? (
                  <span className="text-xs bg-[#25D366] text-white px-2 py-0.5 rounded-full">Live</span>
                ) : (
                  <span className="text-xs text-[#718096]">Soon</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#2D3748] tracking-tight">
              Look as professional as a big company.
            </h2>
            <p className="mt-4 text-lg text-[#718096] leading-relaxed">
              You hustle hard. Wazi makes sure your customers see it - clean quotes, real reviews and a public profile that wins jobs.
            </p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 lift-on-hover" data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className={`w-12 h-12 rounded-xl bg-[#F7FAFC] flex items-center justify-center ${f.color}`}>
                  <f.icon className="w-6 h-6" />
                </div>
                <h3 className="mt-5 font-heading font-semibold text-[#2D3748] text-lg">{f.title}</h3>
                <p className="mt-2 text-sm text-[#718096] leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-20 bg-[#F7FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#2D3748] tracking-tight">
                How Wazi works
              </h2>
              <p className="mt-3 text-[#718096] max-w-xl">From hello to paid in four steps — entirely on WhatsApp.</p>
            </div>
            <Link to="/find-painter" data-testid="how-find-painter-link" className="text-[#1E88E5] font-medium inline-flex items-center hover:underline">
              Try it as a customer <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.num} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 relative" data-testid={`step-${i + 1}`}>
                <div className="font-heading text-5xl font-bold text-[#25D366]/20">{s.num}</div>
                <h3 className="mt-2 font-heading font-semibold text-[#2D3748] text-lg">{s.title}</h3>
                <p className="mt-2 text-sm text-[#718096] leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer demo phone (find painter flow) */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <PhoneSimulator
              role="customer"
              sessionPrefix="landing-customer"
              quickReplies={["Nomvula", "Durbanville", "Repaint living room, 2 bedrooms (~60m²)", "interior", "next week", "YES"]}
            />
          </div>
          <div className="order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 bg-[#1E88E5]/10 text-[#1E88E5] px-3 py-1.5 rounded-full text-xs font-medium font-heading">
              For customers
            </div>
            <h2 className="font-heading mt-4 text-3xl md:text-4xl font-bold text-[#2D3748] tracking-tight">
              Three quotes by lunchtime. Promise.
            </h2>
            <p className="mt-4 text-lg text-[#718096] leading-relaxed">
              Send Wazi a message. Answer a few quick questions. We'll match you with trusted local painters and you'll receive professional written quotes — all on WhatsApp.
            </p>
            <ul className="mt-6 space-y-3">
              {["Real, verified reviews from past customers", "Compare quotes side-by-side", "Pay deposit only when you're happy"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-[#2D3748]">
                  <CheckCircle2 className="w-5 h-5 text-[#25D366] flex-shrink-0 mt-0.5" />
                  <span className="font-sans">{t}</span>
                </li>
              ))}
            </ul>
            <Link to="/find-painter">
              <Button data-testid="customer-section-cta" className="mt-8 rounded-full bg-[#1E88E5] hover:bg-[#1565C0] text-white px-7 py-6 text-base font-medium wazi-blue-glow">
                Find a painter
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-[#F7FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#2D3748] tracking-tight text-center">
            People are talking.
          </h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 lift-on-hover" data-testid={`testimonial-${i}`}>
                <Quote className="w-8 h-8 text-[#25D366]" />
                <p className="mt-4 text-[#2D3748] font-sans leading-relaxed">"{t.text}"</p>
                <div className="mt-5 flex items-center gap-1">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-[#FFB400] fill-[#FFB400]" />)}
                </div>
                <div className="mt-3 text-sm">
                  <div className="font-heading font-semibold text-[#2D3748]">{t.name}</div>
                  <div className="text-[#718096]">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#25D366] rounded-2xl mb-8 wazi-cta-glow">
            <WaziMark size={40} />
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#2D3748] tracking-tight">
            Work smarter. Win more jobs.
          </h2>
          <p className="mt-5 text-lg text-[#718096] max-w-2xl mx-auto">
            Join hundreds of South African painters who are turning chats into contracts on Wazi.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/get-started" onClick={() => trackEvent("cta_click", { location: "footer", audience: "painter" })}>
              <Button data-testid="final-cta-painter" className="rounded-full bg-[#25D366] hover:bg-[#16A34A] text-white px-8 py-6 text-base font-medium wazi-cta-glow w-full sm:w-auto">
                I'm a painter
              </Button>
            </Link>
            <Link to="/find-painter" onClick={() => trackEvent("cta_click", { location: "footer", audience: "customer" })}>
              <Button data-testid="final-cta-customer" variant="outline" className="rounded-full border-2 border-[#2D3748] text-[#2D3748] hover:bg-[#2D3748] hover:text-white px-8 py-6 text-base font-medium w-full sm:w-auto">
                I need a painter
              </Button>
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Landing;
